// TourOverlay — the spotlight and the coach-mark card.
//
// A pure renderer of "current step + wherever its anchor turned out to be".
// TourContext owns which step we're on and what page it lives on; this file
// only finds the anchor, measures it, and puts a card next to it.
//
// THE TWO RULES THAT KEEP IT FROM BREAKING:
//  1. A missing anchor is normal, not an error. Feature gates, an absent
//     printer, a panel still waiting on data — any of it can mean the element
//     never shows. After ANCHOR_TIMEOUT_MS the step degrades to a centred card
//     with the same copy. The tour never stalls and never points at nothing.
//  2. The rect is re-measured on a timer while a step is on screen, so panels
//     that fill in late (printer info, ink levels, a poster grid) drag the
//     spotlight with them instead of leaving it stale.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTour } from '../../contexts/TourContext';
// Brightivity: no desktop title bar in a browser window.
const TITLE_BAR_HEIGHT_EXPORT = 0;
import { GATE_POLL_MS, isGateSatisfied } from './tourRuntime';
import './Tour.css';

const CARD_WIDTH = 380;
const GAP = 18;          // spotlight → card
const EDGE = 16;         // card → viewport
const PAD = 8;           // spotlight padding around the anchor
const ANCHOR_TIMEOUT_MS = 4000;
const POLL_MS = 120;
const REMEASURE_MS = 300;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

function measure(el) {
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PAD,
    left: r.left - PAD,
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
    bottom: r.bottom + PAD,
    right: r.right + PAD,
    cx: r.left + r.width / 2,
    cy: r.top + r.height / 2,
  };
}

// A step's `target` may list several anchors in PRIORITY order, best first —
// e.g. the scan step prefers the state card that's actually on screen and falls
// back to the whole page. Plain querySelector('a, b') would return whichever
// comes first in DOCUMENT order, which is the opposite of what we want, so try
// them one at a time.
function resolveAnchor(target) {
  for (const selector of target.split(',')) {
    const trimmed = selector.trim();
    if (!trimmed) continue;
    const el = document.querySelector(trimmed);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    // A zero-size box is a container that hasn't laid out yet — keep looking.
    if (r.width > 8 && r.height > 8) return el;
  }
  return null;
}

function sameRect(a, b) {
  if (!a || !b) return a === b;
  return Math.abs(a.top - b.top) < 1 && Math.abs(a.left - b.left) < 1
    && Math.abs(a.width - b.width) < 1 && Math.abs(a.height - b.height) < 1;
}

// Preferred side first, then the rest — whichever the card actually fits in.
function pickSide(prefer, rect, cardH, vw, vh, topLimit) {
  const order = [prefer, 'right', 'left', 'bottom', 'top'];
  for (const side of order) {
    if (side === 'right' && vw - rect.right - GAP - EDGE >= CARD_WIDTH) return side;
    if (side === 'left' && rect.left - GAP - EDGE >= CARD_WIDTH) return side;
    if (side === 'bottom' && vh - rect.bottom - GAP - EDGE >= cardH) return side;
    if (side === 'top' && rect.top - GAP - topLimit >= cardH) return side;
  }
  return 'center';
}

function placeCard(side, rect, cardH, vw, vh, topLimit) {
  if (side === 'center') {
    return {
      left: clamp(vw / 2 - CARD_WIDTH / 2, EDGE, vw - CARD_WIDTH - EDGE),
      top: clamp(vh / 2 - cardH / 2, topLimit, vh - cardH - EDGE),
      arrow: null,
    };
  }

  let left;
  let top;
  if (side === 'right') { left = rect.right + GAP; top = rect.cy - cardH / 2; }
  else if (side === 'left') { left = rect.left - GAP - CARD_WIDTH; top = rect.cy - cardH / 2; }
  else if (side === 'bottom') { top = rect.bottom + GAP; left = rect.cx - CARD_WIDTH / 2; }
  else { top = rect.top - GAP - cardH; left = rect.cx - CARD_WIDTH / 2; }

  left = clamp(left, EDGE, vw - CARD_WIDTH - EDGE);
  top = clamp(top, topLimit, Math.max(topLimit, vh - cardH - EDGE));

  // The arrow tracks the anchor's centre along the card's facing edge, pulled
  // in far enough that it never pokes past a rounded corner.
  const arrow = (side === 'left' || side === 'right')
    ? { side, offset: clamp(rect.cy - top, 22, cardH - 22) }
    : { side, offset: clamp(rect.cx - left, 26, CARD_WIDTH - 26) };

  return { left, top, arrow };
}

function TourOverlay() {
  const { t } = useTranslation('tour');
  const { step, index, total, isFirst, isLast, next, back, skip, complete } = useTour();

  const cardRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [resolved, setResolved] = useState(false); // anchor found or timed out
  const [cardHeight, setCardHeight] = useState(200);
  const [pos, setPos] = useState(null);

  const stepId = step?.id || null;
  const target = step?.target || null;
  const prefer = step?.placement || 'right';

  // ── Find the anchor ──────────────────────────────────────────────────────
  useEffect(() => {
    setRect(null);
    setResolved(false);
    setPos(null);
    if (!target) {
      setResolved(true);
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    const deadline = Date.now() + ANCHOR_TIMEOUT_MS;

    const tick = () => {
      if (cancelled) return;
      const el = resolveAnchor(target);
      if (el) {
        const r = el.getBoundingClientRect();
        const offScreen = r.top < TITLE_BAR_HEIGHT_EXPORT || r.bottom > window.innerHeight;
        // behavior:'auto', NOT 'smooth'. Measured in this Electron build
        // (2026-08-14): scrollIntoView with behavior:'smooth' on the Home
        // page's overflow container is a silent no-op — scrollTop never leaves
        // 0 — while 'auto' scrolls correctly. Instant is also what a tour wants:
        // the rect is right on the next measure instead of mid-animation.
        if (offScreen) el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        setRect(measure(el));
        setResolved(true);
        return;
      }
      if (Date.now() > deadline) {
        setResolved(true); // centred fallback — copy still lands
        return;
      }
      timer = setTimeout(tick, POLL_MS);
    };

    // One frame's grace for the page swap this step may have just triggered.
    timer = setTimeout(tick, 60);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [stepId, target]);

  // ── Keep the rect honest while the step is on screen ─────────────────────
  useEffect(() => {
    if (!target || !resolved) return undefined;
    const sync = () => {
      const el = resolveAnchor(target);
      if (!el) return;
      const nextRect = measure(el);
      setRect((prev) => (sameRect(prev, nextRect) ? prev : nextRect));
    };
    const id = setInterval(sync, REMEASURE_MS);
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    return () => {
      clearInterval(id);
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
    };
  }, [target, resolved]);

  // ── Position the card off the measured card height ────────────────────────
  useLayoutEffect(() => {
    if (!resolved) return;
    const height = cardRef.current?.offsetHeight || cardHeight;
    if (height !== cardHeight) setCardHeight(height);
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const topLimit = TITLE_BAR_HEIGHT_EXPORT + EDGE;
    if (!rect) {
      setPos(placeCard('center', null, height, vw, vh, topLimit));
      return;
    }
    const side = pickSide(prefer, rect, height, vw, vh, topLimit);
    setPos(placeCard(side, rect, height, vw, vh, topLimit));
  }, [resolved, rect, prefer, cardHeight]);

  // ── Gate: wait for the user to actually do the thing ─────────────────────
  const gateKey = step?.gate || null;
  const [gateOk, setGateOk] = useState(false);

  useEffect(() => {
    if (!gateKey) { setGateOk(false); return undefined; }
    // Check immediately — a returning user may already have a printer cached or
    // Canva linked, and re-asking them to do it would be nonsense.
    setGateOk(isGateSatisfied(gateKey));
    const id = setInterval(() => setGateOk(isGateSatisfied(gateKey)), GATE_POLL_MS);
    return () => clearInterval(id);
  }, [gateKey, stepId]);

  const locked = Boolean(gateKey) && !gateOk;

  const onNext = useCallback(() => {
    if (locked) return;
    if (isLast) complete();
    else next();
  }, [locked, isLast, complete, next]);

  // "Skip for now" — leaves the gate unsatisfied and moves on. Distinct from
  // "Skip tour": this abandons one setup task, not the walkthrough.
  const onSkipStep = useCallback(() => (isLast ? complete() : next()), [isLast, complete, next]);

  // Esc leaves, arrows walk. Capture phase so a focused control underneath
  // can't swallow them.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); skip(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); if (!isFirst) back(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [skip, onNext, back, isFirst]);

  if (!step) return null;

  const showSpotlight = Boolean(rect) && step.placement !== 'center';
  // An interactive step needs the spotlit control to be genuinely clickable, so
  // the single shield becomes four bands around the hole. Everything outside
  // stays inert — the user can operate the control the step is about and nothing
  // else, which is what keeps a half-finished tour from stranding.
  const passThrough = showSpotlight && step.interactive;

  return (
    <div className="pse-tour-root" role="dialog" aria-modal="true" aria-label={t('a11y.tour')}>
      {/* Click shield: the app behind stays visible but inert, so nothing the
          user clicks can strand the tour half-way through a page swap. */}
      {passThrough ? (
        <>
          <div className="pse-tour-shield" style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top) }} />
          <div className="pse-tour-shield" style={{ top: rect.bottom, left: 0, right: 0, bottom: 0 }} />
          <div className="pse-tour-shield" style={{ top: rect.top, height: rect.height, left: 0, width: Math.max(0, rect.left) }} />
          <div className="pse-tour-shield" style={{ top: rect.top, height: rect.height, left: rect.right, right: 0 }} />
        </>
      ) : (
        <div className={`pse-tour-shield ${showSpotlight ? '' : 'is-dim'}`} style={{ inset: 0 }} />
      )}

      {showSpotlight && (
        <div
          className="pse-tour-spot"
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
        />
      )}

      <div
        ref={cardRef}
        className="pse-tour-card"
        style={{
          width: CARD_WIDTH,
          left: pos?.left ?? -9999,
          top: pos?.top ?? -9999,
          opacity: pos ? 1 : 0,
        }}
      >
        {pos?.arrow && (
          <span className={`pse-tour-arrow pse-tour-arrow-${pos.arrow.side}`} style={{ '--offset': `${pos.arrow.offset}px` }} />
        )}

        <div className="pse-tour-card-head">
          <span className="pse-tour-count">{t('nav.progress', { current: index + 1, total })}</span>
          <button type="button" className="pse-tour-skip" onClick={skip}>{t('nav.skip')}</button>
        </div>

        <h3 className="pse-tour-title">{t(`steps.${step.id}.title`)}</h3>
        <p className="pse-tour-body">{t(`steps.${step.id}.body`)}</p>

        {gateKey && (
          <p className={`pse-tour-gate ${gateOk ? 'is-done' : ''}`}>
            <span className="pse-tour-gate-mark" aria-hidden="true">{gateOk ? '✓' : '•'}</span>
            {t(`gates.${gateKey}.${gateOk ? 'done' : 'waiting'}`)}
          </p>
        )}

        <div className="pse-tour-progress">
          <span className="pse-tour-progress-fill" style={{ width: `${((index + 1) / total) * 100}%` }} />
        </div>

        <div className="pse-tour-actions">
          <button type="button" className="pse-tour-btn pse-tour-btn-ghost" onClick={back} disabled={isFirst}>
            {t('nav.back')}
          </button>
          {/* Only while the gate is unmet — once it's satisfied there is nothing
              left to skip, and the row goes back to two buttons. */}
          {locked && (
            <button type="button" className="pse-tour-btn pse-tour-btn-ghost" onClick={onSkipStep}>
              {t('nav.skipStep')}
            </button>
          )}
          <button type="button" className="pse-tour-btn pse-tour-btn-primary" onClick={onNext} disabled={locked}>
            {isLast ? t('nav.done') : t('nav.next')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TourOverlay;
